import { useMediaQuery, useTheme, Stack, Button } from '@mui/material'
import React from 'react'
import CompanyDeleteButton from './CompanyDeleteButton'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useDeleteCompany from '../hooks/use-delete-company'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useNavigate } from 'react-router-dom'
import GeneratePartnerNumberButton from './GeneratePartnerNumberButton'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'

interface CompanyViewFormActionButtonsProps {
  isDisabledRemoving: boolean
  companyId: number
  companyName: string
  partnerNumber?: string
}

/**
 * Additional buttons for the service user company form
 */
const CompanyViewFormActionButtons: React.FC<CompanyViewFormActionButtonsProps> = ({ isDisabledRemoving, companyId, companyName, partnerNumber }) => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const { breakpoints } = useTheme()
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'))

  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { mutate: deleteCompany, isPending: isDeleteCompanyLoading } = useDeleteCompany({
    onSuccess: () => {
      enqueueSuccessStack(t('service-dashboard.company.delete-company.success'))
      navigate('/overview/companies')
    },
    onError: () => {
      console.error('Could not delete company')
      enqueueErrorStack(t('service-dashboard.company.delete-company.error'))
    },
  })

  const handleDeleteCompany = async (): Promise<void> => {
    if (!companyId) {
      return
    }

    deleteCompany({
      id: companyId,
    })
  }

  const onCreateInstaller = (): void => {
    navigate(`/create-user?companyId=${companyId}`)
  }

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <OptionalTooltipWrapper hasTooltip={isDisabledRemoving} titleKey={t('service-dashboard.company.delete-company.tooltip-blocker-text')}>
        <CompanyDeleteButton
          companyName={companyName}
          handleDeleteCompany={handleDeleteCompany}
          isLoading={isDeleteCompanyLoading}
          fullWidth={isSmallScreen}
          isDisabled={isDisabledRemoving}
        />
      </OptionalTooltipWrapper>
      {!partnerNumber && <GeneratePartnerNumberButton companyId={companyId} fullWidth={isSmallScreen} />}
      <Button variant="outlined" onClick={onCreateInstaller}>
        {t('service-dashboard.company.create-installer')}
      </Button>
    </Stack>
  )
}

export default CompanyViewFormActionButtons
